/**
 * Fase 38.5b — Shipped Fixes registry.
 *
 * Reemplazó al ex-"Regression dataset" del MVP. Ahora cada fila representa
 * un fix shipped (registrado al promover un finding) + el conteo de
 * regressions detectadas pasivamente (no-sintéticas, sin ventana temporal —
 * paridad exacta con el listado que abre el badge). La detección ocurre al
 * final de cada `session.analyze` real — no hay cron diario.
 *
 * Si una fila tiene `regressionCount > 0`, significa que ya volvió a
 * aparecer el bug en sesiones nuevas (click en el badge filtra el inbox).
 */
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  fetchShippedFixes,
  type ShippedFixItem,
} from '../../api/admin/findings.js';
import { shortId } from '../../lib/format.js';

function relTime(iso: string | null): string {
  if (!iso) return '—';
  const delta = Math.max(0, Date.now() - new Date(iso).getTime());
  if (delta < 60_000) return `${Math.floor(delta / 1000)}s ago`;
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  return new Date(iso).toLocaleString();
}

function RegressionCount({ count }: { count: number }): ReactElement {
  if (count === 0) return <span className="badge b-success">0</span>;
  if (count < 3) return <span className="badge b-warn">{count}</span>;
  return <span className="badge b-danger">{count}</span>;
}

export function ShippedFixesPage(): ReactElement {
  const [moduleFilter, setModuleFilter] = useState<string>('');
  const query = useQuery({
    queryKey: ['admin', 'shipped-fixes', moduleFilter],
    queryFn: () => fetchShippedFixes(moduleFilter || undefined),
    refetchInterval: 60_000,
  });

  return (
    <>
      <div className="page-header" style={{ alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Shipped fixes</h1>
          <p className="page-sub">
            Findings promovidos a "vigilancia" tras aplicar el fix. El matcher de regression
            (al final de cada <code className="mono">session.analyze</code> real) levanta un
            counter cuando el bug vuelve a aparecer en sesiones nuevas.
          </p>
        </div>
      </div>

      <section className="sess-filters">
        <span className="label">Módulo</span>
        <input
          className="input"
          placeholder="ej. english"
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value.trim())}
          style={{ width: 160 }}
        />
      </section>

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {(query.error).message}</div>
      ) : (
        <section className="card" style={{ padding: 0 }}>
          {query.data.items.length === 0 ? (
            <div className="empty" style={{ padding: 24 }}>
              Sin fixes shipped registrados. Promové un finding desde su detail page.
            </div>
          ) : (
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Regressions</th>
                  <th>Module</th>
                  <th>Category</th>
                  <th>Expected behavior</th>
                  <th>Origin finding</th>
                  <th>Última reg.</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((it: ShippedFixItem) => (
                  <tr key={it.id}>
                    <td>
                      {it.regressionCount > 0 && it.sourceFindingId ? (
                        <Link to={`/findings?regression_of=${it.sourceFindingId}`}>
                          <RegressionCount count={it.regressionCount} />
                        </Link>
                      ) : (
                        <RegressionCount count={it.regressionCount} />
                      )}
                    </td>
                    <td className="mono">{it.moduleSlug}</td>
                    <td className="mono">{it.category ?? '—'}</td>
                    <td>{it.expectedBehavior}</td>
                    <td>
                      {it.sourceFindingId ? (
                        <Link to={`/findings/${it.sourceFindingId}`}>
                          {shortId(it.sourceFindingId, 8)}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td title={it.latestRegressionAt ?? ''}>{relTime(it.latestRegressionAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </>
  );
}
